import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router";
import { useSearchParams } from "react-router-dom";
import { gql, useLazyQuery, useQuery } from "@apollo/client";
import { useIntl } from "react-intl";
import Skeleton from "react-loading-skeleton";
import { classNames, isotizeDate, toGQLPeriod, useAppConfig, useIsMounted } from "@ct-react/core";
import { castBookingOption } from "@ct-react/calendar";
import { useLocaleNavigate } from "@ct-react/locale";
import { SiteType } from "@shared/site-type";
import {
  ANNUAL_ARTICLE_SUMMARY_FRAGMENT,
  BOOKING_SUGGESTION_FRAGMENT,
  RENTAL_ARTICLE_SUMMARY_FRAGMENT,
  SALE_ARTICLE_SUMMARY_FRAGMENT,
  SEASONAL_ARTICLE_SUMMARY_FRAGMENT
} from "@shared/gql/fragments";
import { useDisplayMobileOnly, useDisplayUntilDesktop } from "../tools/breakpoints";
import {
  AnySearchCriteria,
  FilterSearchCriteria, HomealpMainSearchCriteria,
  isFlexBookingConfig,
  ObjectType,
  SearchPagination,
  SearchQuery,
  SearchViewConfig
} from "../models/search";
import {
  parseSearchParams,
  rebuildQuerySearchParamsFromFilter,
  rebuildQuerySearchParamsFromMain
} from "../tools/search-params/search";
import SeoHelmet from "../components/seo-helmet";
import Wrapper from "../components/layout/wrapper";
import MainSearchForm from "../components/search/main-search-form";
import HomealpSearchForm from "../components/search/homealp-search-form";
import { MapAsyncPopupMarker, MapContainer } from "../code-splitting/map";
import ViewSwitcher from "../components/search/view-switcher";
import CountedResult from "../components/search/counted-result";
import FilterButton from "../components/search/filter-button";
import Summary from "../components/cards/summary";
import ResultPagination from "../components/search/result-pagination";
import "./common.scss";
import "./search.scss";

const AGENCY_CONTEXT_GQL_DATA = gql`
  query GetAgency($agencyId: ID!) {
    agency: cmsRentalpAgencyPage(agencyId: $agencyId) { id name logo }
  }
`;

const SEARCH_GQL_DATA = (type: SiteType) => {
  switch (type) {
    case SiteType.SEASONALP:
      return gql`
        ${SEASONAL_ARTICLE_SUMMARY_FRAGMENT}
        query Search($where: AnyArticleFilterInput, $first: Int, $after: String, $last: Int, $before: String) {
          search(where: $where, first: $first, after: $after, last: $last, before: $before) {
            totalCount
            paginationInfo { currentPage pageCount previousPageCursor nextPageCursor lastPageCursor linkPageCursors { page cursor } }
            edges {
              cursor
              node { ... on SeasonalAccommodationArticle { ...SeasonalArticleSummaryFields } }
            }
            accommodationMapMarkers { id coordinates }
          }
        }
      `;
    case SiteType.HOMEALP:
      return gql`
        ${SALE_ARTICLE_SUMMARY_FRAGMENT}
        ${ANNUAL_ARTICLE_SUMMARY_FRAGMENT}
        query Search($where: AnyArticleFilterInput, $first: Int, $after: String, $last: Int, $before: String) {
          search(where: $where, first: $first, after: $after, last: $last, before: $before) {
            totalCount
            paginationInfo { currentPage pageCount previousPageCursor nextPageCursor lastPageCursor linkPageCursors { page cursor } }
            edges {
              cursor
              node {
                ... on SaleAccommodationArticle { ...SaleArticleSummaryFields }
                ... on AnnualAccommodationArticle { ...AnnualArticleSummaryFields }
              }
            }
            accommodationMapMarkers { id coordinates }
          }
        }
      `;
    default:
      return gql`
        ${RENTAL_ARTICLE_SUMMARY_FRAGMENT}
        ${BOOKING_SUGGESTION_FRAGMENT}
        query Search($where: AnyArticleFilterInput, $first: Int, $after: String, $last: Int, $before: String) {
          search(where: $where, first: $first, after: $after, last: $last, before: $before) {
            totalCount
            paginationInfo { currentPage pageCount previousPageCursor nextPageCursor lastPageCursor linkPageCursors { page cursor } }
            edges {
              cursor
              node { ... on RentalAccommodationArticle { ...RentalArticleSummaryFields } }
              accommodationBookingSuggestion { ...BookingSuggestionFields }
            }
            accommodationMapMarkers { id coordinates }
          }
        }
      `;
  }
}

const rewriteGQLQuery = (type: SiteType, query: SearchQuery) => {
  let { region, agency, period, features, objectTypes, contextType, ...result } = query as any;
  if (!!features) result = { ...result, ...features };
  if (!!region) result.regions = [ +region.id ];
  if (!!agency) result.merchantIds = [ agency ];
  if (!!objectTypes) result.objectTypes = objectTypes.map((ot: ObjectType) => +ot.id);
  if (!!period && type === SiteType.RENTALP) {
    if (isFlexBookingConfig(period)) result.flexPeriod = { ...period, intoMonths: period.intoMonths.map(m => isotizeDate(m, "YYYY-MM")) };
    else result.period = { ...period, period: toGQLPeriod(period.period) };
  }
  return {
    ...(type === SiteType.RENTALP) && { rentalAccommodation: result },
    ...(type === SiteType.SEASONALP) && { seasonalAccommodation: result },
    ...(type === SiteType.HOMEALP && contextType === "sale") && { saleAccommodation: result },
    ...(type === SiteType.HOMEALP && contextType === "rental") && { annualAccommodation: result }
  }
};

const Search = ({ refContext }: { refContext: "agency" | "resort" }) => {

  const intl = useIntl();
  const isMounted = useIsMounted();
  const { options: { siteType, asAgency } } = useAppConfig();
  const navigate = useLocaleNavigate();
  const { slug: resortSlug } = useParams();

  // handle query definition

  const pageSize = useMemo(() => 20, []);

  const handleRefContextOnQuery = useCallback((current: URLSearchParams) => ({
    ...(!!asAgency) && { agency: asAgency.id },
    ...(refContext === "agency") && {
      agency: current.get("refId"),
      ...(current.has("region") && { region: { id: current.get("region") } })
    },
    ...(refContext === "resort" && !!resortSlug && current.has("refId")) && {
      region: { id: +current.get("refId")!, slug: resortSlug }
    }
  }), []);

  const [ searchParams, setSearchParams ] = useSearchParams();
  const { query: initQuery, pagination: initPagination, viewConfig: initViewConfig } = parseSearchParams(searchParams, siteType);
  const [ pagination, setPagination ] = useState<SearchPagination>(initPagination);
  const [ viewConfig, setViewConfig ] = useState<SearchViewConfig>(initViewConfig);
  const [ query, setQuery ] = useState<SearchQuery>({
    ...initQuery,
    ...handleRefContextOnQuery(searchParams)
  } as SearchQuery);

  const paginationQueryParams = useMemo(() => {
    return pagination.direction === "before"
      ? { last: pageSize, before: pagination.cursor! }
      : { first: pageSize, ...(!!pagination.cursor) && { after: pagination.cursor! } };
  }, [ pagination ]);


  // use search params has state ref for any search criteria

  useEffect(() => {
    if (refContext !== "agency") return;
    getAgency({ ssr: false, variables: { agencyId: searchParams.get("refId") } }).then();
  }, [ refContext ]);

  useEffect(() => {
    if (!isMounted) return;
    const parsing = parseSearchParams(searchParams, siteType);
    setViewConfig(parsing.viewConfig);
    setPagination(parsing.pagination);
    setQuery({
      ...parsing.query,
      ...handleRefContextOnQuery(searchParams)
    } as SearchQuery);
  }, [ searchParams ]);

  // data loading

  const { loading, data } = useQuery(SEARCH_GQL_DATA(siteType), {
    ssr: false,
    variables: {
      where: rewriteGQLQuery(siteType, query),
      ...paginationQueryParams
    }
  });

  const [ getAgency, { loading: agencyLoading, data: agencyData } ] = useLazyQuery(AGENCY_CONTEXT_GQL_DATA);


  // display events

  useEffect(() => {
    if (isMounted && viewConfig.mapView && withMobileViewSwitcher)
      mapRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [ viewConfig.mapView ]);


  // dom interactions

  const onSearch = (newQuery: AnySearchCriteria, type: SiteType) => {

    const newParams = new URLSearchParams(searchParams);
    rebuildQuerySearchParamsFromMain(newParams, newQuery, type);
    (refContext === "agency" && !!newQuery.region) && newParams.set("region", newQuery.region.id.toString());
    (refContext === "agency" && newParams.has("region") && !newQuery.region) && newParams.delete("region");

    const needRouting = (refContext === "resort" && query.region?.id !== newQuery.region?.id);
    if (!needRouting) {
      setSearchParams(newParams);
      return;
    }

    newParams.delete("refId");
    let queryParams = !!newQuery.region ? `refId=${newQuery.region.id}` : "";
    if (!!newQuery.region && newParams.size > 0) queryParams += "&";
    if (newParams.size > 0) queryParams += newParams.toString();
    navigate(`/resorts/${newQuery.region?.slug ?? "any"}/search${queryParams.length > 0 ? `?${queryParams}` : ""}`);
  }

  const onFilter = (newQuery: FilterSearchCriteria) => {
    const newParams = new URLSearchParams(searchParams);
    rebuildQuerySearchParamsFromFilter(newParams, newQuery);
    setSearchParams(newParams);
  }

  const onSwitchView = (mapView: boolean) => {
    if (!mapView && searchParams.has("map_view")) searchParams.delete("map_view");
    else searchParams.set("map_view", String(mapView));
    setSearchParams(searchParams, { replace: true });
  }

  const onNavPageChange = () => window.scrollTo({ top: 0, left: 0, behavior: "smooth" });


  // rendering

  const isMobile = useDisplayMobileOnly();
  const untilDesktop = useDisplayUntilDesktop();
  const withMobileViewSwitcher = isMounted && untilDesktop;
  const mapRef = useRef<HTMLElement>(null);

  return (
    <>

      <SeoHelmet title={intl.formatMessage(
        {
          id: "seo-title-search",
          defaultMessage: "{success, select, false {Recherche...} other {Recherche: {count, plural, =0 {aucun résultat} =1 {# résultat} other {# résultats}}}}"
        },
        { success: String(!loading), count: data?.search?.totalCount || 0 }
      )}/>

      <Wrapper.Centered as="main"
                        className="rl-search">

        <div className="rls-form">
          {refContext === "agency" &&
            <h3>
              {(agencyLoading || !agencyData?.agency)
                ? <Skeleton width={isMobile ? "75%" : "35%"}/>
                : agencyData.agency.name
              }
            </h3>
          }
          {siteType === SiteType.HOMEALP
            ? <HomealpSearchForm agencyId={agencyData?.agency.id || asAgency?.id}
                                 criteria={query as HomealpMainSearchCriteria}
                                 onCriteriaChange={onSearch} />
            : <MainSearchForm agencyId={agencyData?.agency.id || asAgency?.id}
                              criteria={query}
                              onCriteriaChange={onSearch} />
          }
        </div>

        <div className={classNames("rls-result-wrapper", { "map-only": viewConfig.mapView })}>

          <section className="rls-result-list">
            <div className={classNames("rls-result-header", { empty: !loading && data?.search?.totalCount === 0 })}>
              <CountedResult loading={loading} data={data?.search?.totalCount}/>
              <FilterButton loading={loading || agencyLoading}
                            agencyId={agencyData?.agency.id || asAgency?.id}
                            countedResult={data?.search?.totalCount}
                            data={query}
                            onDataChange={onFilter}/>
            </div>
            {(!loading && data.search)
              ? <div className="rg-wrapped-list">
                {
                  data.search.edges.map((edge: any) =>
                    <Summary key={edge.node.id}
                             data={{
                               ...edge.node,
                               bookingSuggestion: castBookingOption(edge.accommodationBookingSuggestion)
                             }}/>)
                }
              </div>
              : <Skeleton containerClassName="rg-wrapped-list"
                          className="rg-card-skeleton"
                          count={pageSize}
                          inline={true}/>
            }

            {!!data?.search &&
              <ResultPagination className="rls-result-footer"
                                data={{pageSize, ...data.search.paginationInfo}}
                                onPageChange={onNavPageChange}/>
            }
          </section>

          <aside ref={mapRef} className="rls-result-map">
            <MapContainer center={[46.229301769607815, 7.364792654431519]}
                          zoom={9}
                          zoomControlPosition="topright"
                          layersControlPosition={isMobile ? "topleft" : "bottomleft"}
                          clusterize={true}
                          fitBounds={isMobile ? 25 : 100}>
              {!loading && !!data.search && data.search.accommodationMapMarkers.map((m: any, i: number) =>
                <MapAsyncPopupMarker key={i}
                                     siteType={siteType}
                                     contextQuery={rewriteGQLQuery(siteType, query)}
                                     {...m} />
              )}
            </MapContainer>
            {!withMobileViewSwitcher &&
              <ViewSwitcher mapView={viewConfig.mapView} onChange={onSwitchView}/>
            }
          </aside>

        </div>

      </Wrapper.Centered>

      {withMobileViewSwitcher &&
        <ViewSwitcher forMobile={true} mapView={viewConfig.mapView} onChange={onSwitchView}/>
      }

    </>);

}

export default Search;
